<script>
import { page, batchDel } from '@/api/general/geAttachment'
import GeAttachmentEditDialog from './GeAttachmentEditDialog.vue'
import { downloadFile } from '@/utils'
import { IGNORE_PERMISSION } from '@/utils/constants'

const DEFAULT_PERMISSION = {
  add: [IGNORE_PERMISSION],
  del: [IGNORE_PERMISSION],
  edit: [IGNORE_PERMISSION],
}

// 图片扩展名列表
const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg']

export default {
  name: 'AttachmentImageBlock',
  components: {
    GeAttachmentEditDialog
  },
  props: {
    businessId: {
      type: Number,
      required: false,
      default: null
    },
    businessSubId1: {
      type: Number,
      required: false,
      default: null
    },
    businessSubId2: {
      type: Number,
      required: false,
      default: null
    },
    businessSubId3: {
      type: Number,
      required: false,
      default: null
    },
    businessSubId4: {
      type: Number,
      required: false,
      default: null
    },
    businessAttr1: {
      type: String,
      required: false,
      default: null
    },
    businessAttr2: {
      type: String,
      required: false,
      default: null
    },
    // 参考：$enums.ATTACHMENT_BUSINESS_TYPE.[].value
    businessType: {
      type: Number,
      required: true
    },
    permission: {
      type: Object,
      default: () => ({})
    },
    // 一行显示的列数
    columns: {
      type: Number,
      default: 9
    },
    // 是否显示添加按钮
    showAdd: {
      type: Boolean,
      default: true
    },
    // 是否显示删除按钮
    showDel: {
      type: Boolean,
      default: true
    },
    immediate: {
      type: Boolean,
      default: true
    },
    /**
     * 手动模式：开启后，新增和修改不会操作数据库
     */
    manual: {
      type: Boolean,
      default: false
    },
    formConfig: {
      type: Object,
      default: () => ({
        businessAttr1Show: false,
        businessAttr1Label: '业务属性1',
        businessAttr2Show: false,
        businessAttr2Label: '业务属性2'
      })
    },
    /**
     * 新增时扩展数据
     */
    addExtra: {
      type: Object,
      default: () => ({})
    },
    limit: {
      type: Number,
      required: false,
      default: 9
    }
  },
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      queryParams: {
        businessId: null,
        businessSubId1: null,
        businessSubId2: null,
        businessSubId3: null,
        businessSubId4: null,
        businessType: null,
        page: 1,
        size: 100,
        sort: []
      },
      // 图片预览
      previewShow: false,
      previewUrl: '',
      previewIndex: 0,
    }
  },
  computed: {
    fullPermission() {
      return Object.assign({}, DEFAULT_PERMISSION, this.permission)
    },
    // 获取所有可预览的图片URL列表
    previewUrlList() {
      return this.list
        .filter(item => this.isImage(item.fileExt))
        .map(item => item.fileUrl)
    },
    // 计算item宽度
    itemWidth() {
      return `calc(${100 / this.columns}% - 10px)`
    }
  },
  watch: {},
  mounted() {
    if (this.immediate && this.businessId) {
      this.onQuery()
    }
  },
  methods: {
    /**
     * 获取附件对象数组（用于manual模式下统一处理）
     * @returns {Array} 附件对象数组
     */
    getAttachmentList() {
      return this.list.map(item => {
        // 清除内部临时标记，返回干净的附件对象
        // eslint-disable-next-line no-unused-vars
        const { _id, _add, ...rest } = item
        return {
          ...rest,
          businessId: this.businessId,
          businessSubId1: this.businessSubId1,
          businessSubId2: this.businessSubId2,
          businessSubId3: this.businessSubId3,
          businessSubId4: this.businessSubId4,
          businessAttr1: this.businessAttr1,
          businessAttr2: this.businessAttr2,
          businessType: this.businessType
        }
      })
    },
    clearAttachmentList() {
      this.list = []
    },
    onQuery() {
      if (this.manual) {
        return
      }
      this.loading = true
      const params = {
        ...this.queryParams,
        businessId: this.businessId,
        businessSubId1: this.businessSubId1,
        businessSubId2: this.businessSubId2,
        businessSubId3: this.businessSubId3,
        businessSubId4: this.businessSubId4,
        businessAttr1: this.businessAttr1,
        businessAttr2: this.businessAttr2,
        businessType: this.businessType
      }
      page(params).then(res => {
        this.list = res.data.records
        this.total = res.data.total
      }).finally(() => {
        this.loading = false
      })
    },
    onRefresh() {
      this.queryParams = this.$options.data().queryParams
      this.onQuery()
    },
    onAdd() {
      this.$refs.geAttachmentEditDialog.open()
    },
    // 判断是否是图片文件
    isImage(ext) {
      if (!ext) {return false}
      return IMAGE_EXTENSIONS.includes(ext.toLowerCase())
    },
    // 获取缩略图URL
    getThumbnailUrl(item) {
      if (this.isImage(item.fileExt)) {
        return item.fileUrl
      }
      // 非图片文件显示文件图标
      return null
    },
    // 获取文件图标
    getFileIcon(ext) {
      if (!ext) {return 'el-icon-document'}
      const extLower = ext.toLowerCase()
      if (['pdf'].includes(extLower)) {return 'el-icon-document'}
      if (['doc', 'docx'].includes(extLower)) {return 'el-icon-document'}
      if (['xls', 'xlsx'].includes(extLower)) {return 'el-icon-document'}
      if (['ppt', 'pptx'].includes(extLower)) {return 'el-icon-document'}
      if (['zip', 'rar', '7z'].includes(extLower)) {return 'el-icon-folder'}
      if (['mp4', 'avi', 'mov'].includes(extLower)) {return 'el-icon-video-camera'}
      if (['mp3', 'wav', 'flac'].includes(extLower)) {return 'el-icon-headset'}
      return 'el-icon-document'
    },
    // 点击缩略图预览
    onPreview(item) {
      if (this.isImage(item.fileExt)) {
        // 找到当前图片在图片列表中的索引
        const imageIndex = this.list
          .filter(i => this.isImage(i.fileExt))
          .findIndex(i => i.fileUrl === item.fileUrl)
        this.previewIndex = imageIndex >= 0 ? imageIndex : 0
        this.previewUrl = item.fileUrl
        this.previewShow = true
      } else {
        // 非图片文件，下载
        downloadFile(item.fileUrl, item.fileName)
      }
    },
    // 删除附件
    onRowDelete(row) {
      if (this.manual) {
        this.list = this.list.filter(item => item._id !== row._id)
        return
      }
      this.$confirm(this.$t('general.confirm.delete.message', { key: row.fileName }), this.$t('general.confirm.title'), {
        confirmButtonText: this.$t('general.confirm.confirm'),
        cancelButtonText: this.$t('general.confirm.cancel'),
        type: 'warning',
        beforeClose: (action, instance, done) => {
          if (action === 'confirm') {
            instance.confirmButtonLoading = true
            batchDel([row.id]).then(() => {
              this.$message.success(this.$t('general.confirm.delete.success'))
              done()
              this.onRefresh()
            }).finally(() => {
              instance.confirmButtonLoading = false
            })
          } else {
            done()
          }
        }
      })
    },
    onEditComplete(row) {
      if (this.manual) {
        if (row._add) {
          this.list.push(row)
        } else {
          this.list = this.list.map(item => {
            if (item._id === row._id) {
              return row
            }
            return item
          })
        }
        return
      }
      this.onRefresh()
    }
  }
}
</script>

<template>
  <div class="attachment-image-block" v-loading="loading">
    <div class="image-list">
      <!-- 附件缩略图列表 -->
      <div
        v-for="item in list"
        :key="item.id || item._id"
        class="image-item"
        :style="{ width: itemWidth }"
      >
        <div class="image-wrapper" @click="onPreview(item)">
          <!-- 图片缩略图 -->
          <img
            v-if="isImage(item.fileExt)"
            :src="getThumbnailUrl(item)"
            :alt="item.fileName"
            class="thumbnail"
          />
          <!-- 非图片文件显示图标 -->
          <div v-else class="file-icon-wrapper">
            <i :class="getFileIcon(item.fileExt)" class="file-icon"></i>
            <span class="file-ext">.{{ item.fileExt }}</span>
          </div>
        </div>
        <!-- 删除按钮 -->
        <div
          v-if="showDel"
          v-permissions="fullPermission.del"
          class="delete-btn"
          @click.stop="onRowDelete(item)"
        >
          <i class="el-icon-close"></i>
        </div>
        <!-- 文件名称 -->
        <div class="file-name" :title="item.fileName">{{ item.fileName }}</div>
      </div>

      <!-- 添加按钮 -->
      <div
        v-if="showAdd && (limit > list.length)"
        v-permissions="fullPermission.add"
        class="image-item add-item"
        :style="{ width: itemWidth }"
        @click="onAdd"
      >
        <div class="add-wrapper">
          <i class="el-icon-plus add-icon"></i>
        </div>
      </div>
    </div>

    <!-- 图片预览 -->
    <el-image-viewer
      v-if="previewShow"
      :url-list="previewUrlList"
      :initial-index="previewIndex"
      :on-close="() => previewShow = false"
      append-to-body
    ></el-image-viewer>

    <!-- 上传附件弹窗 -->
    <ge-attachment-edit-dialog
      ref="geAttachmentEditDialog"
      :business-id="businessId"
      :business-sub-id1="businessSubId1"
      :business-sub-id2="businessSubId2"
      :business-sub-id3="businessSubId3"
      :business-sub-id4="businessSubId4"
      :business-attr1="businessAttr1"
      :business-attr2="businessAttr2"
      :business-type="businessType"
      :formConfig="formConfig"
      :manual="manual"
      :add-extra="addExtra"
      @complete="onEditComplete"
    />
  </div>
</template>

<style lang="scss" scoped>
.attachment-image-block {
  width: 100%;

  .image-list {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  .image-item {
    position: relative;
    aspect-ratio: 1 / 1;
    border: 1px solid #dcdfe6;
    border-radius: 6px;
    overflow: hidden;
    background-color: #fafafa;
    transition: all 0.3s;

    &:hover {
      border-color: #409eff;
      box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);

      .delete-btn {
        opacity: 1;
      }
    }

    .image-wrapper {
      width: 100%;
      height: calc(100% - 24px);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      overflow: hidden;

      .thumbnail {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .file-icon-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        .file-icon {
          font-size: 40px;
          color: #909399;
        }

        .file-ext {
          margin-top: 4px;
          font-size: 12px;
          color: #909399;
        }
      }
    }

    .delete-btn {
      position: absolute;
      top: 4px;
      right: 4px;
      width: 20px;
      height: 20px;
      background-color: rgba(0, 0, 0, 0.5);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      opacity: 0;
      transition: opacity 0.3s;

      i {
        font-size: 12px;
        color: #fff;
      }

      &:hover {
        background-color: #f56c6c;
      }
    }

    .file-name {
      height: 24px;
      line-height: 24px;
      padding: 0 4px;
      font-size: 12px;
      color: #606266;
      text-align: center;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      background-color: #f5f7fa;
    }
  }

  .add-item {
    cursor: pointer;
    border-style: dashed;

    .add-wrapper {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;

      .add-icon {
        font-size: 28px;
        color: #909399;
      }
    }

    &:hover {
      border-color: #409eff;

      .add-icon {
        color: #409eff;
      }
    }
  }
}
</style>
